'use client';

import { useEffect, useRef, useState } from 'react';
import { TURNSTILE_SCRIPT_SRC } from './SpamGuardFields';

/**
 * The gateway's spam-check inputs (services/api-gateway lib/lead-spam.ts) for the JS lead
 * blocks that post JSON (home valuation, calculators, community match, search alert): the trap
 * field, when the form was shown, the Turnstile token and `blt_ts`, the form's own report on its
 * widget. With Turnstile on, a submit with no token is spam unless `blt_ts` says the widget
 * couldn't run here, so every failure path below sets it to `error`.
 *
 * Nothing here stops the form from sending: a flagged lead is still saved, under Spam.
 */

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

type WidgetState = 'on' | 'off' | 'error';

/** Cloudflare's api.js, shared with any other form on the page (BlockRenderer loads it too). */
function loadTurnstile(timeoutMs = 10_000): Promise<TurnstileApi> {
  const w = window as unknown as { turnstile?: TurnstileApi };
  if (w.turnstile) return Promise.resolve(w.turnstile);
  return new Promise((resolve, reject) => {
    let script = document.querySelector<HTMLScriptElement>(`script[src^="${TURNSTILE_SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = `${TURNSTILE_SCRIPT_SRC}?render=explicit`;
      script.async = true;
      document.head.appendChild(script);
    }
    const started = Date.now();
    script.addEventListener('error', () => reject(new Error('turnstile')));
    // Another form's script tag may already have loaded or be loading; poll for the global.
    const tick = () => {
      if (w.turnstile) return resolve(w.turnstile);
      if (Date.now() - started > timeoutMs) return reject(new Error('turnstile'));
      setTimeout(tick, 100);
    };
    tick();
  });
}

export interface SpamGuard {
  /** Where the Turnstile widget renders (hidden unless Cloudflare needs the visitor). A callback
   *  ref, so a form that appears later (a gate step) still gets its widget. */
  widgetRef: (el: HTMLDivElement | null) => void;
  hasWidget: boolean;
  /** The fields to add to the JSON body, read from the form at submit time. */
  fields: (form: HTMLFormElement) => { blt_hp?: string; form_started_at: number; turnstile_token?: string; blt_ts: WidgetState };
  /** A token works once: call after each submit so a retry gets a fresh one. */
  reset: () => void;
}

export function useSpamGuard(siteKey?: string | null): SpamGuard {
  const startedAt = useRef<number>(Date.now());
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const token = useRef('');
  const state = useRef<WidgetState>(siteKey ? 'on' : 'off');

  useEffect(() => {
    state.current = siteKey ? 'on' : 'off';
    if (!siteKey || !box) return;
    let cancelled = false;
    const fail = () => {
      state.current = 'error';
      token.current = '';
      box.style.display = 'none';
    };
    loadTurnstile()
      .then((api) => {
        if (cancelled || widgetId.current) return;
        widgetId.current = api.render(box, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          retry: 'never',
          callback: (t: string) => { token.current = t; },
          'expired-callback': () => { token.current = ''; },
          'error-callback': () => { fail(); return true; },
          'unsupported-callback': fail,
        });
      })
      .catch(() => { if (!cancelled) fail(); });
    return () => {
      cancelled = true;
      token.current = '';
      const id = widgetId.current;
      widgetId.current = null;
      if (id) {
        try { (window as unknown as { turnstile?: TurnstileApi }).turnstile?.remove(id); } catch { /* already gone */ }
      }
    };
  }, [siteKey, box]);

  return {
    widgetRef: setBox,
    hasWidget: Boolean(siteKey),
    fields(form) {
      const hp = new FormData(form).get('blt_hp');
      return {
        blt_hp: typeof hp === 'string' && hp ? hp : undefined,
        form_started_at: startedAt.current,
        turnstile_token: token.current || undefined,
        blt_ts: state.current,
      };
    },
    reset() {
      token.current = '';
      const id = widgetId.current;
      if (id) {
        try { (window as unknown as { turnstile?: TurnstileApi }).turnstile?.reset(id); } catch { /* gone */ }
      }
    },
  };
}

/** The trap field and the widget's box, for inside the guarded <form>. */
export function SpamGuardInputs({ guard }: { guard: SpamGuard }) {
  return (
    <>
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
        <label>
          Leave this field empty
          <input type="text" name="blt_hp" tabIndex={-1} autoComplete="off" defaultValue="" />
        </label>
      </div>
      {guard.hasWidget ? <div ref={guard.widgetRef} /> : null}
    </>
  );
}
